import { useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy, TrendingUp, Users, IndianRupee } from "lucide-react";

const REGION_FILTER = (orders, region) => {
  if (!region || region === "ALL") return orders;
  return orders.filter((o) => {
    const r = (o.region_id || o.party_region || "").toUpperCase();
    return r.includes(region.toUpperCase()) || r === region;
  });
};

const formatCurrency = (paise) => {
  const rupees = (paise || 0) / 100;
  if (rupees >= 10000000) return `₹${(rupees / 10000000).toFixed(2)}Cr`;
  if (rupees >= 100000) return `₹${(rupees / 100000).toFixed(2)}L`;
  if (rupees >= 1000) return `₹${(rupees / 1000).toFixed(1)}K`;
  return `₹${rupees.toFixed(0)}`;
};

/**
 * Build a 7-day sparkline series from orders (orders/day count).
 */
const buildSparkline = (orders, days = 14) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const series = Array.from({ length: days }, (_, i) => {
    const d = new Date(today);
    d.setDate(d.getDate() - (days - 1 - i));
    const key = d.toISOString().slice(0, 10);
    return { date: key, count: 0 };
  });
  const idx = Object.fromEntries(series.map((s, i) => [s.date, i]));
  for (const o of orders) {
    const ts = new Date(o.created_at || o.order_date || 0);
    if (Number.isNaN(ts.getTime())) continue;
    const k = ts.toISOString().slice(0, 10);
    if (k in idx) series[idx[k]].count += 1;
  }
  return series;
};

const Sparkline = ({ series, color = "#8B1A1A" }) => {
  const w = 110;
  const h = 32;
  const max = Math.max(1, ...series.map((s) => s.count));
  const pts = series
    .map((s, i) => `${(i / (series.length - 1)) * w},${h - (s.count / max) * (h - 4) - 2}`)
    .join(" ");
  const last = series[series.length - 1];
  return (
    <svg width={w} height={h} className="shrink-0">
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="1.6"
        strokeLinejoin="round"
        strokeLinecap="round"
        points={pts}
      />
      {/* fill gradient under line */}
      <polygon
        points={`0,${h} ${pts} ${w},${h}`}
        fill={color}
        opacity="0.08"
      />
      {/* last point dot */}
      <circle
        cx={w}
        cy={h - (last.count / max) * (h - 4) - 2}
        r="2.2"
        fill={color}
      />
    </svg>
  );
};

/**
 * SalesmanPerformancePanel — top 5 salesmen by order count + revenue for the
 * currently filtered region. Shares the orders + employees props from
 * OrdersPage so no extra API call is required.
 */
export const SalesmanPerformancePanel = ({ orders = [], employees = [], activeRegion = "ALL" }) => {
  const data = useMemo(() => {
    const filtered = REGION_FILTER(orders, activeRegion);
    const empMap = Object.fromEntries(employees.map((e) => [e.employee_id, e]));

    const groups = {};
    for (const o of filtered) {
      // try multiple shapes — placed_by, placed_by_name, salesman_id, employee_id
      const id =
        o.placed_by ||
        o.salesman_id ||
        o.employee_id ||
        o.placed_by_id ||
        "";
      const name =
        empMap[id]?.full_name ||
        empMap[id]?.name ||
        o.placed_by_name ||
        o.salesman_name ||
        (id ? id.replace(/^EMP/, "Emp ") : "Mobile App / Dealer");
      const key = id || `name:${name}`;
      if (!groups[key]) {
        groups[key] = {
          key,
          employee_id: id,
          name,
          code: empMap[id]?.employee_code || "",
          orders: [],
          count: 0,
          revenue_paise: 0,
          approved: 0,
          on_hold: 0,
          delivered: 0,
        };
      }
      groups[key].orders.push(o);
      groups[key].count += 1;
      groups[key].revenue_paise += o.total_amount || 0;
      const s = (o.status || "").toUpperCase();
      if (s === "APPROVED") groups[key].approved += 1;
      else if (s === "ON_HOLD") groups[key].on_hold += 1;
      else if (s === "DELIVERED") groups[key].delivered += 1;
    }

    const list = Object.values(groups);
    list.sort(
      (a, b) =>
        b.count - a.count || b.revenue_paise - a.revenue_paise || a.name.localeCompare(b.name),
    );
    const top = list.slice(0, 5).map((g) => ({
      ...g,
      sparkline: buildSparkline(g.orders),
    }));
    const totals = {
      salesmen: list.length,
      orders: list.reduce((s, g) => s + g.count, 0),
      revenue: list.reduce((s, g) => s + g.revenue_paise, 0),
    };
    return { top, totals };
  }, [orders, employees, activeRegion]);

  if (data.totals.orders === 0) {
    return (
      <Card className="p-8 text-center dark:bg-slate-800" data-testid="salesman-performance-empty">
        <Trophy className="w-10 h-10 text-slate-300 mx-auto mb-2" />
        <p className="text-sm text-slate-500 dark:text-slate-400">
          No orders found for this region yet — switch region or wait for the team to start placing orders.
        </p>
      </Card>
    );
  }

  const colors = ["#F59E0B", "#94A3B8", "#B45309", "#0EA5E9", "#10B981"];

  return (
    <div className="space-y-4" data-testid="salesman-performance-panel">
      {/* Summary tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="p-3 dark:bg-slate-800">
          <div className="flex items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400">
            <Users className="w-3.5 h-3.5" /> Active Salesmen ({activeRegion === "ALL" ? "All India" : activeRegion})
          </div>
          <p className="text-2xl font-bold mt-1 font-mono dark:text-white">{data.totals.salesmen}</p>
        </Card>
        <Card className="p-3 dark:bg-slate-800">
          <div className="flex items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400">
            <TrendingUp className="w-3.5 h-3.5" /> Total Orders
          </div>
          <p className="text-2xl font-bold mt-1 font-mono dark:text-white">{data.totals.orders}</p>
        </Card>
        <Card className="p-3 dark:bg-slate-800">
          <div className="flex items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400">
            <IndianRupee className="w-3.5 h-3.5" /> Total Revenue
          </div>
          <p className="text-2xl font-bold mt-1 font-mono dark:text-white">
            {formatCurrency(data.totals.revenue)}
          </p>
        </Card>
      </div>

      {/* Top 5 leaderboard */}
      <Card className="p-4 dark:bg-slate-800" data-testid="top-salesmen-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-500" />
            <h3 className="font-semibold text-sm dark:text-white">Top 5 Salesmen by Orders</h3>
          </div>
          <Badge variant="outline" className="text-[10px]">
            14-day trend
          </Badge>
        </div>
        <div className="space-y-2">
          {data.top.map((g, i) => {
            const ratio = data.top[0].count > 0 ? g.count / data.top[0].count : 0;
            return (
              <div
                key={g.key}
                className="flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/40"
                data-testid={`salesman-rank-${i}`}
              >
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs text-white shrink-0"
                  style={{ background: colors[i] || "#64748B" }}
                >
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium dark:text-white truncate">{g.name}</p>
                    {g.code && <Badge variant="outline" className="text-[10px] shrink-0">{g.code}</Badge>}
                  </div>
                  {/* Progress bar relative to leader */}
                  <div className="mt-1.5 h-1.5 w-full bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${ratio * 100}%`, background: colors[i] || "#64748B" }}
                    />
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    Approved {g.approved} · On hold {g.on_hold} · Delivered {g.delivered}
                  </div>
                </div>
                <Sparkline series={g.sparkline} color={colors[i] || "#64748B"} />
                <div className="text-right shrink-0 min-w-[110px]">
                  <p className="text-sm font-bold font-mono dark:text-white">{g.count} <span className="text-[10px] text-slate-400">orders</span></p>
                  <p className="text-[11px] font-mono text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(g.revenue_paise)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

export default SalesmanPerformancePanel;
